import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal, Select } from '@/components/ui'
import { useClubPeers, useLendBook } from '@/hooks/loans'
import { todayISO } from '@/lib/utils'
import type { Book } from '@/types'

const SOMEONE_ELSE = '__someone_else__'

/** Lend a book to a club friend (they get a copy in their library) or to anyone by name */
export function LendBookModal({ book, onClose }: { book: Book; onClose: () => void }) {
  const { t } = useTranslation()
  const { data: peers = [], isLoading } = useClubPeers()
  const lend = useLendBook()
  const [borrower, setBorrower] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [lentOn, setLentOn] = useState(todayISO())

  const isOutsider = borrower === SOMEONE_ELSE || (!isLoading && peers.length === 0)
  const canSubmit = isOutsider ? !!name.trim() : !!borrower

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    await lend.mutateAsync({
      bookId: book.id,
      borrowerId: isOutsider ? null : borrower,
      borrowerName: isOutsider ? name.trim() : null,
      lentOn,
    })
    onClose()
  }

  return (
    <Modal isOpen onClose={onClose} title={t('loans.lendTitle', { title: book.title })}>
      <form onSubmit={submit} className="space-y-4 p-6">
        {peers.length > 0 && (
          <div className="space-y-2">
            <label htmlFor="lend-borrower" className="text-sm font-medium text-foreground">
              {t('loans.borrower')}
            </label>
            <Select
              id="lend-borrower"
              value={borrower}
              onChange={setBorrower}
              placeholder={t('loans.chooseFriend')}
              options={[
                ...peers.map((p) => ({ value: p.user_id, label: p.display_name })),
                { value: SOMEONE_ELSE, label: t('loans.someoneElse') },
              ]}
            />
            {!isOutsider && borrower && (
              <p className="text-xs text-muted-foreground">{t('loans.friendHint')}</p>
            )}
          </div>
        )}

        {isOutsider && (
          <div className="space-y-2">
            <label htmlFor="lend-name" className="text-sm font-medium text-foreground">
              {t('loans.borrowerName')}
            </label>
            <Input
              id="lend-name"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('loans.borrowerNamePlaceholder')}
              autoFocus
            />
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="lend-date" className="text-sm font-medium text-foreground">
            {t('loans.lentOn')}
          </label>
          <Input
            id="lend-date"
            type="date"
            value={lentOn}
            max={todayISO()}
            onChange={(e) => setLentOn(e.target.value)}
          />
        </div>

        {lend.isError && <p className="text-sm text-error">{t('errors.general')}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!canSubmit || lend.isPending}>
            {lend.isPending ? t('common.saving') : t('loans.lend')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
